import React from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { usePortalAuth } from "../../context/PortalAuthContext";
import PortalChatWidget from "../../components/portal/PortalChatWidget";
import { usePublicBranding } from "../../hooks/usePublicBranding";
import { getPortalSubdomain } from "../../utils/portalSubdomain";

export const PortalProtectedRoute: React.FC = () => {
  const { isAuthenticated } = usePortalAuth();
  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }
  return <PortalShell />;
};

const PortalShell: React.FC = () => {
  const { user, me, logout } = usePortalAuth();
  const navigate = useNavigate();
  const subdomain = me?.subdomain || user?.subdomain || getPortalSubdomain();
  const { branding } = usePublicBranding(subdomain);

  const displayName = branding?.businessName || branding?.companyName || user?.operatorBusinessName || "Your credit team";

  const linkClass = ({ isActive }: { isActive: boolean }) => {
    const base =
      "border-b-2 px-3 py-2 text-sm font-medium text-white/90 transition-colors hover:text-white";
    if (isActive) {
      return `${base} border-[var(--portal-accent)] text-white`;
    }
    return `${base} border-transparent`;
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-800">
      <header className="border-b border-white/10 bg-[var(--portal-primary)]">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            {branding?.logoUrl ? (
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5">
                <img src={branding.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
              </div>
            ) : null}
            <div>
              <p className="text-sm font-semibold text-white">{displayName}</p>
              <p className="text-xs text-navy-100">Client portal</p>
            </div>
          </div>
          <nav className="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
            <NavLink to="/portal/dashboard" className={linkClass}>
              Home
            </NavLink>
            <NavLink to="/portal/disputes" className={linkClass}>
              My disputes
            </NavLink>
            <NavLink to="/portal/documents" className={linkClass}>
              My documents
            </NavLink>
            <button
              type="button"
              onClick={() => {
                logout();
                navigate("/portal/login", { replace: true });
              }}
              className="rounded-md px-3 py-2 text-left text-sm font-medium text-white/90 transition-colors hover:bg-black/20 hover:text-white sm:text-center"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      <main className="mx-auto min-w-0 max-w-full px-4 py-8 pb-28 sm:max-w-5xl">
        <Outlet />
      </main>

      {branding?.aiChatbotEnabled !== false ? <PortalChatWidget /> : null}
    </div>
  );
};
